import React, { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Checkbox, Box,
  Table, TableHead, TableRow, TableCell, TableBody
} from "@mui/material";
import { useLazyQuery, useMutation } from "@apollo/client";
import { GET_ALL_FEATURES, ADD_ROLE, UPDATE_ROLE } from "../queries";
import { enqueueSnackbar } from "notistack";

interface FeatureAccess {
  create_access: boolean;
  read_access: boolean;
  update_access: boolean;
  delete_access: boolean;
}

interface RoleDialogProps {
  open: boolean;
  onClose: () => void;
  mode: "create" | "edit";
  selectedRole?: {
    role_id: string;
    role_name: string;
    features: {
      feature_id: string;
      feature_name: string;
      create_access: boolean;
      read_access: boolean;
      update_access: boolean;
      delete_access: boolean;
    }[];
  };
  createdBy: string;
}

const RoleDialog: React.FC<RoleDialogProps> = ({
  open,
  onClose,
  mode,
  selectedRole,
  createdBy,
}) => {
  const [step, setStep] = useState(1);
  const [roleName, setRoleName] = useState("");
  const [features, setFeatures] = useState<any[]>([]);
  const [featureAccess, setFeatureAccess] = useState<{ [id: string]: FeatureAccess }>({});


  const [getFeatures] = useLazyQuery(GET_ALL_FEATURES, {
    onCompleted: (data) => {
      setFeatures(data.getAllFeatures);
    },
  });
  const [createRole] = useMutation(ADD_ROLE, {
    onCompleted: () => {
      enqueueSnackbar("Role created successfully!", { variant: "success" });
      handleReset();
      onClose();
    },
    onError: (err) => {
      console.error("Create Role Error:", err.message);
      enqueueSnackbar(`Create failed: ${err.message}`, { variant: "error" });
    },
  });

  const [updateRole] = useMutation(UPDATE_ROLE, {
    onCompleted: () => {
      enqueueSnackbar("Role updated successfully!", { variant: "success" });
      handleReset();
      onClose();
    },
    onError: (err) => {
      console.error("Update Role Error:", err.message);
      enqueueSnackbar(`Update failed: ${err.message}`, { variant: "error" });
    },
  });

  // Fetch features when dialog opens
  useEffect(() => {
    if (open) getFeatures();
  }, [open]);


  // Sync feature access when features + role are loaded
  useEffect(() => {
    if (open && features.length > 0) {
      setStep(1);
      if (mode === "edit" && selectedRole) {
        setRoleName(selectedRole.role_name);
        const accessMap: { [id: string]: FeatureAccess } = {};
        selectedRole.features.forEach((f) => {
          accessMap[f.feature_id] = {
            create_access: f.create_access,
            read_access: f.read_access,
            update_access: f.update_access,
            delete_access: f.delete_access,
          };
        });
        setFeatureAccess(accessMap);

      } else {
        setRoleName("");
        setFeatureAccess({});
      }
    }
  }, [features, open, selectedRole, mode]);


  const toggleAccess = (id: string, type: keyof FeatureAccess) => {
    setFeatureAccess((prev) => {
      const current = prev[id] || {
        create_access: false,
        read_access: false,
        update_access: false,
        delete_access: false,
      };

      const newValue = !current[type];
      const updatedAccess = {
        ...current,
        [type]: newValue,
      };

      // If any create/update/delete permission is selected, automatically select read
      if (newValue && type !== "read_access") {
        updatedAccess.read_access = true;
      }

      // If read_access is unselected, typically other permissions should also be unselected
      if (!newValue && type === "read_access") {
        updatedAccess.create_access = false;
        updatedAccess.update_access = false;
        updatedAccess.delete_access = false;
      }

      return {
        ...prev,
        [id]: updatedAccess,
      };
    });
  };


  const handleSubmit = () => {
    const formattedFeatures = Object.entries(featureAccess)
      .map(([featureId, access]) => ({
        featureId,
        create_access: access.create_access,
        read_access: access.read_access,
        update_access: access.update_access,
        delete_access: access.delete_access,
        isActive: true,
      }));


    if (mode === "create") {
      createRole({
        variables: {
          roleName: roleName.trim(),
          features: formattedFeatures,
        },
      });
    } else if (mode === "edit" && selectedRole) {
      updateRole({
        variables: {
          roleId: selectedRole.role_id,
          roleName: roleName.trim(),
          createdBy,
          features: formattedFeatures,
        },
      });
    }
  };

  const handleReset = () => {
    setStep(1);
    setRoleName("");
    setFeatures([]);
    setFeatureAccess({});
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{step === 1 ? `${mode === "edit" ? "Edit" : "Create"} Role` : "Assign Features"}</DialogTitle>
      <DialogContent>
        {step === 1 ? (
          <TextField
            fullWidth
            label="Role Name"
            value={roleName}
            onChange={(e) => setRoleName(e.target.value)}
            margin="normal"
          />
        ) : (
          <Box>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Feature</strong></TableCell>
                  <TableCell align="center"><strong>Create</strong></TableCell>
                  <TableCell align="center"><strong>Read</strong></TableCell>
                  <TableCell align="center"><strong>Update</strong></TableCell>
                  <TableCell align="center"><strong>Delete</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {features.map((feature) => {
                  const access = featureAccess[feature.feature_id] || {
                    create_access: false,
                    read_access: false,
                    update_access: false,
                    delete_access: false,
                  };
                  return (
                    <TableRow key={feature.feature_id}>
                      <TableCell>{feature.feature_name}</TableCell>
                      <TableCell align="center">
                        <Checkbox
                          checked={access.create_access}
                          onChange={() => toggleAccess(feature.feature_id, "create_access")}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          checked={access.read_access}
                          onChange={() => toggleAccess(feature.feature_id, "read_access")}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          checked={access.update_access}
                          onChange={() => toggleAccess(feature.feature_id, "update_access")}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          checked={access.delete_access}
                          onChange={() => toggleAccess(feature.feature_id, "delete_access")}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {step === 2 && (
          <Button onClick={() => setStep(1)}>Back</Button>
        )}
        {step === 1 ? (
          <Button
            onClick={() => setStep(2)}
            disabled={!roleName.trim()}
            variant="contained"
          >
            Next
          </Button>
        ) : (
          <Button variant="contained" onClick={handleSubmit}>
            Submit
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default RoleDialog;
